/**
 * ParentSignIn — magic-link sign-in for the parent dashboard.
 *
 * Mirrors the SLP-side AuthScreen posture but uses the
 * `useParentAuth` context (which sets `emailRedirectTo` to
 * `${window.location.origin}/parent/dashboard` rather than the
 * clinician's `/auth/callback`). The parent never sees the SLP-side
 * auth flow.
 *
 * Per the project rules (effectiveslp-ios rule #5 — plain language),
 * the page copy is intentionally non-clinical. No "data collection",
 * "IEP", "session", "clinician" jargon. "Speech therapist" → "your
 * SLP" stays because that's how SLPs self-identify to families.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useParentAuth } from '../../lib/auth/useParentAuth';
import { BrandMonogram } from '../../lib/effectiveslp/BrandMonogram';

type Status = 'idle' | 'sending' | 'sent' | 'error';

interface Props {
  initialEmail?: string;
}

export function ParentSignIn({ initialEmail = '' }: Props): ReactElement {
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const { signInWithEmail } = useParentAuth();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email address.');
      return;
    }
    setStatus('sending');
    setError(null);
    try {
      await signInWithEmail({ email: trimmed });
      setStatus('sent');
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'Could not send magic link.';
      setError(message);
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <main className="screen auth">
        <div className="auth-hero">
          <BrandMonogram size={64} />
          <header className="screen-header">
            <h1>Check your email</h1>
            <p className="tagline">
              We sent a sign-in link to {email.trim()}.
            </p>
          </header>
        </div>

        <section className="auth-confirm" aria-live="polite">
          <p>
            Open the email on this device and tap the link to see how
            your kid is doing. The link expires in 1 hour.
          </p>
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setStatus('idle');
              setError(null);
            }}
          >
            Use a different email
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="screen auth parent">
      <div className="auth-hero">
        <BrandMonogram size={64} />
        <header className="screen-header">
          <h1>Sign in to see your kid&rsquo;s practice</h1>
          <p className="tagline">
            We&rsquo;ll email you a one-tap sign-in link.
          </p>
        </header>
      </div>

      <form className="auth-form" onSubmit={(e) => void handleSubmit(e)}>
        <label>
          Email
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'sending'}
            required
          />
        </label>

        <button
          type="submit"
          className="snap-button"
          disabled={status === 'sending'}
        >
          {status === 'sending' ? 'Sending…' : 'Send magic link'}
        </button>

        {error && <p className="error">{error}</p>}
      </form>

      <p className="tagline">
        Messages here are not for emergencies. If you need urgent help,
        contact your SLP directly or call 911.
      </p>
    </main>
  );
}
